import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import PrepBusinessService from "../../../services/api/prepbusiness/prepbusiness";

const CliqueTab = () => {
  const [cliques, setCliques] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingClique, setEditingClique] = useState(null);

  // Notification State
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // Form State
  const [name, setName] = useState("");
  const [membersInput, setMembersInput] = useState("");

  useEffect(() => {
    // Try to load from local storage first
    const cachedCliques = localStorage.getItem("cliques_cache");
    if (cachedCliques) {
      try {
        setCliques(JSON.parse(cachedCliques));
      } catch (e) {
        console.error("Failed to parse cached cliques", e);
      }
    }

    // Always fetch fresh data on mount (or you could rely on cache and require manual refresh)
    fetchCliques();
  }, []);

  const showNotification = (message, severity = "success") => {
    setNotification({ open: true, message, severity });
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const fetchCliques = async () => {
    setLoading(true);
    try {
      const response = await PrepBusinessService.getCliques();
      // Handle potential response variations (axios data wrapper vs direct data)
      const data = response.data || response;
      const validData = Array.isArray(data) ? data : [];

      setCliques(validData);
      // Update local storage
      localStorage.setItem("cliques_cache", JSON.stringify(validData));
    } catch (error) {
      showNotification("Failed to load cliques", "error");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (clique = null) => {
    if (clique) {
      setEditingClique(clique);
      setName(clique.name || "");
      setMembersInput(clique.members ? clique.members.join(", ") : "");
    } else {
      setEditingClique(null);
      setName("");
      setMembersInput("");
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Convert comma-separated string to array
    const membersArray = membersInput
      .split(",")
      // Split by comma OR newline to allow pasting lists
      .flatMap((m) => m.split(/\n/))
      .map((m) => m.trim())
      .filter((m) => m.length > 0);

    if (membersArray.length === 0) {
      showNotification(
        "Please add at least one member (e.g. Rituals, Zalando)",
        "warning",
      );
      return;
    }

    const payload = {
      name: name,
      members: membersArray,
    };

    try {
      if (editingClique) {
        await PrepBusinessService.updateClique(editingClique.id, payload);
        showNotification("Combination rule updated", "success");
      } else {
        await PrepBusinessService.createClique(payload);
        showNotification("New combination rule created", "success");
      }
      setIsModalOpen(false);
      fetchCliques();
    } catch (error) {
      showNotification("Operation failed", "error");
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this rule?")) return;

    try {
      await PrepBusinessService.deleteClique(id);
      showNotification("Rule deleted", "success");
      fetchCliques();
    } catch (error) {
      showNotification("Delete failed", "error");
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box>
          <Typography variant="h6" component="h2">
            Bundle Combination Rules
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Define sets of "EK-Orte" that can be packed together in the same
            box.
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            onClick={fetchCliques}
            startIcon={
              loading ? <CircularProgress size={20} /> : <RefreshIcon />
            }
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenModal()}
          >
            Add Rule
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center" width="80">
                ID
              </TableCell>
              <TableCell>Name / Category</TableCell>
              <TableCell>Allowed Combinations (Example Members)</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : cliques.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="textSecondary">
                    No combination rules found. Default hardcoded rules might be
                    active.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              cliques.map((row) => (
                <TableRow key={row.id}>
                  <TableCell align="center">{row.id}</TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="medium">
                      {row.name || "Unnamed Group"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      {row.members &&
                        row.members.map((member, idx) => (
                          <Chip
                            key={idx}
                            label={member}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenModal(row)}
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(row.id)}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editingClique ? "Edit Combination Rule" : "New Combination Rule"}
          </DialogTitle>
          <DialogContent dividers>
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                label="Group Name (Optional)"
                placeholder="e.g. Cosmetics, Electronics..."
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                variant="outlined"
              />

              <Box>
                <TextField
                  label="Allowed Members (Comma Separated)"
                  placeholder="Rituals, Zalando, Parfumdreams..."
                  value={membersInput}
                  onChange={(e) => setMembersInput(e.target.value)}
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                  helperText='Enter the exact "EK-Ort" names that are allowed to be bundled together.'
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsModalOpen(false)} color="inherit">
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary">
              {editingClique ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CliqueTab;
